import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useOutletContext } from "react-router-dom";
import type { SessionUser } from "../../types/auth";
import {
  waitlistGetSettings,
  waitlistSetSmsPhase,
  waitlistSetGoogleReviewUrl,
  waitlistSetReviewAutoSend,
  waitlistUpdateTable,
  type SmsPhase,
  type WaitlistTable,
  type WaitlistSettings,
} from "../../lib/api";
import ReservationSettingsSection from "./ReservationSettingsSection";

const SMS_PHASE_LABELS: Record<SmsPhase, string> = {
  off:          "Off",
  intake_only:  "Intake Only",
  full:         "Full",
};

const SMS_PHASE_DESC: Record<SmsPhase, string> = {
  off:         "No SMS sent — UI-only mode. Use for initial setup and validation.",
  intake_only: "Sends intake confirmation only. Use to validate delivery before enabling all messages.",
  full:        "All SMS enabled: intake, ready, final call, seated, and review messages.",
};

interface OutletCtx { user: SessionUser }

export default function HostSettings() {
  const { user } = useOutletContext<OutletCtx>();
  const isOwner = user.bar_roles.includes("bar_owner");

  const [settings, setSettings]   = useState<WaitlistSettings | null>(null);
  const [tables, setTables]       = useState<WaitlistTable[]>([]);
  const [loading, setLoading]     = useState(true);
  const [loadErr, setLoadErr]     = useState<string | null>(null);

  // SMS phase state
  const [phaseWorking, setPhaseWorking] = useState(false);
  const [phaseMsg, setPhaseMsg]         = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmPhase, setConfirmPhase] = useState<SmsPhase | null>(null);

  // Review URL state
  const [urlDraft, setUrlDraft]   = useState("");
  const [urlWorking, setUrlWorking] = useState(false);
  const [urlMsg, setUrlMsg]       = useState<{ ok: boolean; text: string } | null>(null);

  const phaseBusyRef = useRef(false);
  const urlBusyRef = useRef(false);
  const reviewBusyRef = useRef(false);
  const tableBusyRef = useRef(new Set<string>());
  const [reviewWorking, setReviewWorking] = useState(false);
  const [reviewMsg, setReviewMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Table edit state: tableId → { min, max, is_active, saving, msg }
  const [tableEdits, setTableEdits] = useState<Record<string, {
    min: number; max: number; is_active: boolean; saving: boolean; msg: string | null
  }>>({});

  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    setLoading(true);
    waitlistGetSettings()
      .then(({ settings: s, tables: t }) => {
        if (!mountedRef.current) return;
        setSettings(s);
        setUrlDraft(s.google_review_url ?? "");
        const edits: typeof tableEdits = {};
        for (const tbl of t) {
          edits[tbl.id] = { min: tbl.min_capacity, max: tbl.max_capacity, is_active: tbl.is_active, saving: false, msg: null };
        }
        setTableEdits(edits);
        setTables(t);
        setLoading(false);
      })
      .catch((e) => {
        if (!mountedRef.current) return;
        setLoadErr(e.message ?? "Failed to load settings");
        setLoading(false);
      });
  }, []);

  /* ── SMS phase ── */
  async function applyPhase(phase: SmsPhase) {
    if (phaseBusyRef.current) return;
    phaseBusyRef.current = true;
    setConfirmPhase(null);
    setPhaseWorking(true);
    setPhaseMsg(null);
    try {
      const result = await waitlistSetSmsPhase(phase);
      if (!mountedRef.current) return;
      setSettings((s) => s ? { ...s, sms_phase: result.sms_phase } : s);
      setPhaseMsg({ ok: true, text: `SMS phase set to "${SMS_PHASE_LABELS[result.sms_phase]}"` });
    } catch (e: any) {
      if (!mountedRef.current) return;
      setPhaseMsg({ ok: false, text: e.message ?? "Failed to update phase" });
    } finally {
      phaseBusyRef.current = false;
      if (mountedRef.current) setPhaseWorking(false);
    }
  }

  function requestPhaseChange(phase: SmsPhase) {
    if (phaseBusyRef.current || phase === settings?.sms_phase) return;
    if (phase === "full") { setConfirmPhase(phase); return; }
    void applyPhase(phase);
  }

  /* ── Review URL ── */
  async function saveUrl() {
    if (urlBusyRef.current) return;
    const url = urlDraft.trim();
    try {
      if (!["http:", "https:"].includes(new URL(url).protocol)) throw new Error();
    } catch {
      setUrlMsg({ ok: false, text: "Enter a valid http or https URL." });
      return;
    }
    urlBusyRef.current = true;
    setUrlWorking(true);
    setUrlMsg(null);
    try {
      const result = await waitlistSetGoogleReviewUrl(url);
      if (!mountedRef.current) return;
      setSettings((s) => s ? { ...s, google_review_url: result.google_review_url } : s);
      setUrlDraft(result.google_review_url);
      setUrlMsg({ ok: true, text: "Saved" });
    } catch (e: any) {
      if (!mountedRef.current) return;
      setUrlMsg({ ok: false, text: e.message ?? "Failed" });
    } finally {
      urlBusyRef.current = false;
      if (mountedRef.current) setUrlWorking(false);
    }
  }

  /* ── Review auto-send toggle ── */
  async function toggleAutoSend() {
    if (!settings || reviewBusyRef.current) return;
    reviewBusyRef.current = true;
    setReviewWorking(true);
    setReviewMsg(null);
    const next = !settings.review_sms_auto_send_enabled;
    try {
      const result = await waitlistSetReviewAutoSend(next);
      if (!mountedRef.current) return;
      setSettings((s) => s ? { ...s, review_sms_auto_send_enabled: result.review_sms_auto_send_enabled } : s);
      setReviewMsg({ ok: true, text: result.review_sms_auto_send_enabled ? "Automatic review SMS enabled." : "Automatic review SMS disabled." });
    } catch (e: any) {
      if (mountedRef.current) setReviewMsg({ ok: false, text: e.message ?? "Failed to save automatic review SMS setting." });
    } finally {
      reviewBusyRef.current = false;
      if (mountedRef.current) setReviewWorking(false);
    }
  }

  /* ── Table edits ── */
  function patchTableEdit(id: string, patch: Partial<typeof tableEdits[string]>) {
    setTableEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  async function saveTable(tbl: WaitlistTable) {
    if (tableBusyRef.current.has(tbl.id)) return;
    const edit = tableEdits[tbl.id];
    if (!edit) return;
    // Number("") is 0 → previously passed the (min > max) check when both
    // fields were blanked, silently saving a 0-capacity table. Reject any
    // sub-1 values up front.
    if (!Number.isInteger(edit.min) || edit.min < 1) { patchTableEdit(tbl.id, { msg: "Min must be a whole number ≥ 1" }); return; }
    if (!Number.isInteger(edit.max) || edit.max < 1 || edit.max > 99) { patchTableEdit(tbl.id, { msg: "Max must be a whole number from 1 to 99" }); return; }
    if (edit.min > edit.max) { patchTableEdit(tbl.id, { msg: "Min must be ≤ max" }); return; }
    tableBusyRef.current.add(tbl.id);
    patchTableEdit(tbl.id, { saving: true, msg: null });
    try {
      await waitlistUpdateTable(tbl.id, {
        min_capacity: edit.min,
        max_capacity: edit.max,
        is_active:    edit.is_active,
      });
      if (!mountedRef.current) return;
      setTables((prev) => prev.map((t) => t.id === tbl.id
        ? { ...t, min_capacity: edit.min, max_capacity: edit.max, is_active: edit.is_active }
        : t
      ));
      patchTableEdit(tbl.id, { saving: false, msg: "✓ Saved" });
      setTimeout(() => { if (mountedRef.current) patchTableEdit(tbl.id, { msg: null }); }, 2000);
    } catch (e: any) {
      if (!mountedRef.current) return;
      patchTableEdit(tbl.id, { saving: false, msg: e.message ?? "Failed" });
    } finally {
      tableBusyRef.current.delete(tbl.id);
    }
  }

  /* ── Render ── */
  if (loading) return <main style={s.page}><p style={s.muted}>Loading settings…</p></main>;
  if (loadErr) return <main style={s.page}><p style={s.error}>{loadErr}</p></main>;
  if (!settings) return null;

  const rooms = [...new Set(tables.map((t) => t.room_id))];

  return (
    <main style={s.page}>
      <h1 style={s.h1}>Host Settings</h1>

      {/* ── SMS Phase ── */}
      <section style={s.section}>
        <h2 style={s.h2}>SMS Phase</h2>
        <p style={s.desc}>{SMS_PHASE_DESC[settings.sms_phase]}</p>
        <div style={s.phaseRow}>
          {(["off", "intake_only", "full"] as SmsPhase[]).map((phase) => (
            <button
              key={phase}
              type="button"
              disabled={phaseWorking}
              onClick={() => requestPhaseChange(phase)}
              style={{
                ...s.phaseBtn,
                ...(settings.sms_phase === phase ? s.phaseBtnActive : {}),
              }}
            >
              {SMS_PHASE_LABELS[phase]}
            </button>
          ))}
        </div>
        {phaseMsg && (
          <p style={phaseMsg.ok ? s.ok : s.error}>{phaseMsg.text}</p>
        )}
      </section>

      {/* ── Review auto-send (owner only) ── */}
      {isOwner && (
        <section style={s.section}>
          <h2 style={s.h2}>Review SMS Auto-Send</h2>
          <div style={s.toggleRow}>
            <label style={s.toggleLabel} htmlFor="review-auto-send">
              Send review SMS automatically after check close
            </label>
            <button
              id="review-auto-send"
              type="button"
              role="switch"
              aria-checked={settings.review_sms_auto_send_enabled}
              onClick={toggleAutoSend}
              disabled={reviewWorking}
              aria-busy={reviewWorking}
              style={{
                ...s.toggle,
                ...(settings.review_sms_auto_send_enabled ? s.toggleOn : s.toggleOff),
              }}
            >
              {reviewWorking ? "Saving…" : settings.review_sms_auto_send_enabled ? "ON" : "OFF"}
            </button>
          </div>
          {reviewMsg && <p role="status" style={reviewMsg.ok ? s.ok : s.error}>{reviewMsg.text}</p>}
          <p style={s.desc}>
            When on, review requests send 10 min after the check closes (respects quiet hours and guest consent).
          </p>
        </section>
      )}

      {/* ── Google Review URL (owner only) ── */}
      {isOwner && (
        <section style={s.section}>
          <h2 style={s.h2}>Google Review URL</h2>
          <div style={s.urlRow}>
            <input
              type="url"
              disabled={urlWorking}
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="https://g.page/r/.../review"
              style={s.input}
              aria-label="Google review URL"
            />
            <button
              type="button"
              onClick={saveUrl}
              disabled={urlWorking}
              style={s.btnPrimary}
            >
              {urlWorking ? "Saving…" : "Save"}
            </button>
          </div>
          {urlMsg && <p style={urlMsg.ok ? s.ok : s.error}>{urlMsg.text}</p>}
        </section>
      )}

      {/* ── Table capacity editor (owner only) ── */}
      {isOwner && (
        <section style={s.section}>
          <h2 style={s.h2}>Table Capacities</h2>
          {rooms.map((roomId) => (
            <div key={roomId} style={s.roomBlock}>
              <h3 style={s.h3}>{roomId.charAt(0).toUpperCase() + roomId.slice(1)}</h3>
              {tables.filter((t) => t.room_id === roomId).map((tbl) => {
                const edit = tableEdits[tbl.id];
                if (!edit) return null;
                return (
                  <div key={tbl.id} style={s.tableRow}>
                    <div style={s.tableLabel}>
                      <span style={s.tableName}>{tbl.label}</span>
                      <span style={edit.is_active ? s.activeBadge : s.inactiveBadge}>
                        {edit.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div style={s.tableControls}>
                      <label style={s.fieldLabel}>
                        Min
                        <input
                          type="number"
                          min={1}
                          max={edit.max}
                          value={edit.min}
                          disabled={edit.saving}
                          onChange={(e) => patchTableEdit(tbl.id, { min: Number(e.target.value) })}
                          style={s.numInput}
                          aria-label={`${tbl.label} minimum capacity`}
                        />
                      </label>
                      <label style={s.fieldLabel}>
                        Max
                        <input
                          type="number"
                          min={edit.min}
                          max={99}
                          value={edit.max}
                          disabled={edit.saving}
                          onChange={(e) => patchTableEdit(tbl.id, { max: Number(e.target.value) })}
                          style={s.numInput}
                          aria-label={`${tbl.label} maximum capacity`}
                        />
                      </label>
                      <label style={s.fieldLabel}>
                        Active
                        <input
                          type="checkbox"
                          checked={edit.is_active}
                          disabled={edit.saving}
                          onChange={(e) => patchTableEdit(tbl.id, { is_active: e.target.checked })}
                          style={{ marginTop: 4 }}
                          aria-label={`${tbl.label} active`}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => saveTable(tbl)}
                        disabled={edit.saving}
                        style={s.btnSmall}
                      >
                        {edit.saving ? "…" : "Save"}
                      </button>
                      {edit.msg && (
                        <span style={edit.msg.startsWith("✓") ? s.ok : s.error}>{edit.msg}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </section>
      )}

      {/* ── Reservation platform settings (owner only) ── */}
      {isOwner && <ReservationSettingsSection />}

      {/* ── Full SMS phase confirmation modal ── */}
      {confirmPhase && (
        <div style={s.modalOverlay} role="dialog" aria-modal="true" aria-label="Confirm SMS phase change">
          <div style={s.modal}>
            <p style={s.modalTitle}>Enable Full SMS?</p>
            <p style={s.modalBody}>
              This enables <strong>all waitlist SMS messages</strong>: ready, final call, seated notifications,
              and review requests. Confirm the intake SMS works correctly before enabling Full.
            </p>
            <div style={s.modalBtns}>
              <button type="button" onClick={() => setConfirmPhase(null)} style={s.btnSecondary}>
                Cancel
              </button>
              <button type="button" onClick={() => applyPhase("full")} disabled={phaseWorking} style={s.btnDanger}>
                Enable Full SMS
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

const s: Record<string, CSSProperties> = {
  page:         { padding: 16, maxWidth: 640 },
  h1:           { fontSize: 18, fontWeight: 800, color: "var(--text)", margin: "0 0 20px", letterSpacing: -0.3 },
  h2:           { fontSize: 13, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 8px" },
  h3:           { fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 8px" },
  section:      { borderBottom: "1px solid var(--border)", paddingBottom: 20, marginBottom: 20 },
  desc:         { fontSize: 12, color: "var(--muted)", margin: "0 0 12px", lineHeight: 1.5 },
  muted:        { color: "var(--muted)", fontSize: 13 },
  ok:           { color: "#27ae60", fontSize: 12, margin: "4px 0 0" },
  error:        { color: "#E05040", fontSize: 12, margin: "4px 0 0" },
  phaseRow:     { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 },
  phaseBtn:     {
    padding: "8px 16px", borderRadius: 8, border: "1px solid var(--border)",
    background: "transparent", color: "var(--muted)", fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  phaseBtnActive: { borderColor: "var(--gold)", color: "var(--gold)", background: "rgba(249,175,54,0.08)" },
  toggleRow:    { display: "flex", alignItems: "center", gap: 12, marginBottom: 8 },
  toggleLabel:  { fontSize: 13, color: "var(--text)", flex: 1 },
  toggle:       {
    padding: "4px 12px", borderRadius: 20, border: "none", fontSize: 11,
    fontWeight: 800, letterSpacing: 0.5, cursor: "pointer", minWidth: 48,
  },
  toggleOn:     { background: "#27ae60", color: "#fff" },
  toggleOff:    { background: "var(--border)", color: "var(--muted)" },
  urlRow:       { display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" },
  input:        {
    flex: 1, padding: "8px 12px", borderRadius: 6, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 16,
  },
  btnPrimary:   {
    padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--gold)",
    color: "#0D0B08", fontWeight: 800, fontSize: 12, cursor: "pointer",
  },
  btnSecondary: {
    padding: "8px 16px", borderRadius: 6, border: "1px solid var(--border)",
    background: "transparent", color: "var(--muted)", fontWeight: 700, fontSize: 12, cursor: "pointer",
  },
  btnDanger:    {
    padding: "8px 16px", borderRadius: 6, border: "none", background: "#E05040",
    color: "#fff", fontWeight: 800, fontSize: 12, cursor: "pointer",
  },
  btnSmall:     {
    padding: "4px 10px", borderRadius: 6, border: "1px solid var(--border)",
    background: "transparent", color: "var(--gold)", fontWeight: 700, fontSize: 11, cursor: "pointer",
  },
  roomBlock:    { marginBottom: 12 },
  tableRow:     {
    display: "flex", alignItems: "center", flexWrap: "wrap",
    gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border)",
  },
  tableLabel:   { display: "flex", alignItems: "center", gap: 8, minWidth: 140 },
  tableName:    { fontSize: 13, fontWeight: 700, color: "var(--text)" },
  activeBadge:  {
    fontSize: 9, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
    padding: "2px 6px", borderRadius: 10, background: "rgba(39,174,96,0.15)", color: "#27ae60",
  },
  inactiveBadge: {
    fontSize: 9, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
    padding: "2px 6px", borderRadius: 10, background: "rgba(176,160,140,0.15)", color: "var(--muted)",
  },
  tableControls: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  fieldLabel:   { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, fontSize: 10, color: "var(--muted)", fontWeight: 600 },
  numInput:     {
    width: 52, padding: "4px 6px", borderRadius: 4, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13, textAlign: "center",
  },
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16,
  },
  modal:        {
    background: "var(--bg)", border: "1px solid var(--border)",
    borderRadius: 12, padding: 24, maxWidth: 360, width: "100%",
  },
  modalTitle:   { fontSize: 16, fontWeight: 800, color: "var(--text)", margin: "0 0 8px" },
  modalBody:    { fontSize: 13, color: "var(--muted)", margin: "0 0 20px", lineHeight: 1.5 },
  modalBtns:    { display: "flex", gap: 8, justifyContent: "flex-end" },
};
